import React, { type ReactNode } from 'react';

import AdminPanelSettingsOutlinedIcon from '@mui/icons-material/AdminPanelSettingsOutlined';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import ScienceOutlinedIcon from '@mui/icons-material/ScienceOutlined';
import ToggleOnOutlinedIcon from '@mui/icons-material/ToggleOnOutlined';
import VisibilityOffOutlinedIcon from '@mui/icons-material/VisibilityOffOutlined';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Card,
  CardContent,
  Divider,
  Stack,
  Typography,
} from '@mui/material';

import { type PromoFeatureRoleContext } from '@luxuryescapes/contract-svc-promo';

import { usePromoFeaturesConfig } from '~/hooks/usePromoFeatureAvailability';

import PromoFeatureHeader from './PromoFeatureHeader';
import { findPromoFeatureIndicator } from './PromoTester/promoFeatureIndicators';

export type PromoFeaturePresentation = 'box' | 'accordion' | 'raw';

interface Props {
  /** A contract feature key or an Admin Portal-only feature key. */
  featureKey: string;
  /** Defaults to the signed-in user's auth roles when omitted. */
  authRoles?: PromoFeatureRoleContext['authRoles'];
  /** Pass loaded promo roles; omit while their source is unresolved. */
  promoRoles?: PromoFeatureRoleContext['promoRoles'];
  /** `raw` has no outer surface; use it inside an existing card or accordion. */
  presentation?: PromoFeaturePresentation;
  fallbackTitle?: string;
  children: ReactNode;
}

type ConfigurationItem = {
  icon: ReactNode;
  label: string;
};

function getConfigurationItems(
  feature: NonNullable<ReturnType<typeof findPromoFeatureIndicator>>,
): ReadonlyArray<ConfigurationItem> {
  const isExperimental = feature.docType !== 'feature';
  const requiredRoleNames = feature.requiredRoles?.roles
    .filter((requirement) => requirement.type === 'auth_role' || requirement.type === 'promo_role')
    .map((requirement) => requirement.key);
  const roleLabel = requiredRoleNames?.join(feature.requiredRoles?.operator === 'and' ? ' and ' : ' or ');

  return [
    ...(roleLabel
      ? [
          {
            icon: <AdminPanelSettingsOutlinedIcon fontSize="small" />,
            label: roleLabel,
          },
        ]
      : []),
    ...(isExperimental
      ? [
          {
            icon: <ScienceOutlinedIcon fontSize="small" />,
            label: 'Experimental',
          },
        ]
      : []),
    ...(feature.isOnlyTest
      ? [
          {
            icon: <VisibilityOffOutlinedIcon fontSize="small" />,
            label: 'Test only',
          },
        ]
      : []),
    ...(feature.canOptIn
      ? [
          {
            icon: <ToggleOnOutlinedIcon fontSize="small" />,
            label: 'Opt-in',
          },
        ]
      : []),
    ...(feature.isFunctionallyComplete
      ? [
          {
            icon: <CheckCircleOutlineIcon fontSize="small" />,
            label: 'Complete',
          },
        ]
      : []),
  ];
}

/** Presents compact contract-owned feature status; service-side access remains enforced by the owning API. */
export default function PromoFeature({
  featureKey,
  authRoles,
  promoRoles,
  presentation = 'box',
  fallbackTitle,
  children,
}: Props) {
  const { config } = usePromoFeaturesConfig({ authRoles, promoRoles });
  const feature = findPromoFeatureIndicator(config.features, featureKey);

  // Do not hide configuration fields if an installed contract is older than the page.
  if (!feature) {
    if (presentation === 'accordion' && fallbackTitle) {
      return (
        <Accordion variant="outlined">
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>{fallbackTitle}</AccordionSummary>
          <AccordionDetails>{children}</AccordionDetails>
        </Accordion>
      );
    }
    return <>{children}</>;
  }

  const configurationItems = getConfigurationItems(feature);
  const header = <PromoFeatureHeader feature={feature} configurationItems={configurationItems} />;

  if (presentation === 'raw') {
    return (
      <Stack spacing={2}>
        {header}
        {children}
      </Stack>
    );
  }

  if (presentation === 'accordion') {
    return (
      <Accordion variant="outlined">
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>{header}</AccordionSummary>
        <AccordionDetails>
          <Stack spacing={2}>
            <Stack spacing={0.5}>
              <Typography variant="body2">{feature.description}</Typography>
              <Typography variant="body2" color="text.secondary">
                {feature.currentStatusHeadline}
              </Typography>
              {feature.notes && (
                <Typography variant="body2" color="text.secondary">
                  {feature.notes}
                </Typography>
              )}
            </Stack>
            {children}
          </Stack>
        </AccordionDetails>
      </Accordion>
    );
  }

  return (
    <Card
      variant="outlined"
      sx={(theme) => ({
        width: '100%',
        borderColor: theme.palette.mode === 'dark' ? theme.palette.action.disabled : theme.palette.divider,
      })}
    >
      <CardContent sx={{ '&:last-child': { pb: 2 } }}>
        <Stack spacing={2}>
          {header}
          <Divider />
          {children}
        </Stack>
      </CardContent>
    </Card>
  );
}
